import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';

const History = () => {
  const { updateCredits } = useAuth();
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const response = await api.get('/sms/history');
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to load messages:', error);
      toast.error('Failed to load message history');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (messageId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled message? Your credit will be refunded.')) {
      return;
    }

    try {
      const response = await api.post(`/sms/cancel/${messageId}`);
      updateCredits(response.data.creditsRemaining);
      toast.success('Message cancelled. Credit refunded.');
      loadMessages();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel message');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: { bg: 'rgba(34, 139, 34, 0.2)', color: '#90EE90', text: '✓ Sent' },
      scheduled: { bg: 'rgba(255, 215, 0, 0.2)', color: '#FFD700', text: '📅 Scheduled' },
      pending: { bg: 'rgba(168, 181, 200, 0.2)', color: '#a8b5c8', text: '⏳ Pending' },
      failed: { bg: 'rgba(196, 30, 58, 0.2)', color: '#ff6b6b', text: '✗ Failed' },
      cancelled: { bg: 'rgba(102, 102, 102, 0.2)', color: '#999', text: '⊘ Cancelled' },
      processing: { bg: 'rgba(0, 191, 255, 0.2)', color: '#00BFFF', text: '⟳ Processing' }
    };
    const style = styles[status] || styles.pending;
    
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '15px',
        background: style.bg,
        color: style.color,
        fontSize: '0.85rem',
        fontWeight: 500
      }}>
        {style.text}
      </span>
    );
  };

  const getSenderEmoji = (senderId) => {
    const emojis = {
      NorthPole: '🎄',
      Santa: '🎅',
      ElfOnShelf: '🧝',
      Frosty: '⛄',
      Rudolph: '🦌',
      Sleigh: '🛷',
      NewYear: '🎆',
      HNY: '🥳',
      '2026': '✨'
    };
    return emojis[senderId] || '📱';
  };

  const filteredMessages = messages.filter(m => {
    if (filter === 'all') return true;
    return m.status === filter;
  });

  if (loading) {
    return (
      <div className="main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="hero" style={{ padding: '20px' }}>
        <h1 className="hero-title" style={{ fontSize: '2.5rem' }}>
          📜 Message History
        </h1>
        <p className="hero-subtitle">
          View all your sent and scheduled messages
        </p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="tabs" style={{ borderBottom: 'none', marginBottom: 0 }}>
          <button
            className={`tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({messages.length})
          </button>
          <button
            className={`tab ${filter === 'sent' ? 'active' : ''}`}
            onClick={() => setFilter('sent')}
          >
            Sent ({messages.filter(m => m.status === 'sent').length})
          </button>
          <button
            className={`tab ${filter === 'scheduled' ? 'active' : ''}`}
            onClick={() => setFilter('scheduled')}
          >
            Scheduled ({messages.filter(m => m.status === 'scheduled').length})
          </button>
          <button
            className={`tab ${filter === 'failed' ? 'active' : ''}`}
            onClick={() => setFilter('failed')}
          >
            Failed ({messages.filter(m => m.status === 'failed').length})
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="card">
        {filteredMessages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px', color: 'var(--text-secondary)' }}>
            <p style={{ fontSize: '4rem', marginBottom: '15px' }}>📭</p>
            <p style={{ fontSize: '1.1rem' }}>No messages found</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {filteredMessages.map(message => (
              <div
                key={message.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '15px',
                  padding: '15px',
                  border: '1px solid rgba(255, 215, 0, 0.1)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px' }}>
                    <span style={{ fontSize: '1.3rem' }}>
                      {getSenderEmoji(message.senderId)}
                    </span>
                    <strong style={{ color: 'var(--christmas-gold)', fontSize: '0.9rem' }}>{message.senderId}</strong>
                    <span style={{ color: 'var(--text-secondary)' }}>→</span>
                    <span style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>{message.recipientPhone}</span>
                  </div>
                  {getStatusBadge(message.status)}
                </div>
                
                <div style={{ 
                  background: 'rgba(0, 0, 0, 0.2)', 
                  padding: '12px', 
                  borderRadius: '10px',
                  marginBottom: '12px',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5,
                  fontSize: '0.9rem',
                  wordBreak: 'break-word'
                }}>
                  {message.content}
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                    {message.status === 'scheduled' && (
                      <span>📅 Scheduled for: {formatDate(message.scheduledAt)}</span>
                    )}
                    {message.status === 'sent' && (
                      <span>✓ Sent: {formatDate(message.sentAt)}</span>
                    )}
                    {message.status === 'failed' && (
                      <span style={{ color: '#ff6b6b' }}>⚠️ Error: {message.errorMessage || 'Unknown error'}</span>
                    )}
                    {!['scheduled', 'sent', 'failed'].includes(message.status) && (
                      <span>Created: {formatDate(message.createdAt)}</span>
                    )}
                  </div>
                  
                  {message.status === 'scheduled' && (
                    <button
                      onClick={() => handleCancel(message.id)}
                      className="btn btn-outline"
                      style={{ padding: '6px 15px', fontSize: '0.85rem' }}
                    >
                      Cancel & Refund
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
